/**
 * Social accounts (Figma 09 · Social accounts 01–21, states 06–19).
 * Sign-in opens in its own window and this page waits (socialOAuthPopup.ts);
 * a blocked window falls back to the full-page redirect. The OAuth callbacks
 * still land on ?page=connections&connected=…|socialError=…|moved=….
 *
 * Not built, no backend yet: sending the connect step to the client and the
 * "waiting for the client" request, picking which Pages after signing in (the
 * backend adds every Page the login manages), "Connected by", the list of
 * scheduled posts (the count is shown), Sync now / results not updating,
 * choosing where paused posts go (the backend moves them), Undo on moved
 * posts, and "Ask the owner to reconnect".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CalendarX,
  ChartLine,
  Check,
  Ellipsis,
  LoaderCircle,
  RefreshCw,
  TriangleAlert,
  Unplug,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import { queue } from "@/components/Toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganizationMembers } from "@/app/routes/dashboard/settings/api/useOrganization";
import {
  useConnectFacebook,
  useConnectInstagram,
  useConnectTikTok,
  useConnectYouTube,
  useDisconnectSocialConnection,
  useSocialConnections,
  useSocialConnectionStats,
  type SocialAccount,
  type SocialConnection,
  type SocialProvider,
} from "@/app/routes/dashboard/settings/api/useSocialConnections";
import { usePublishableAccounts } from "../../../content-calendar/api/useScheduling";
import { KitButton } from "../../kit/Button";
import { KitIconButton } from "../../kit/IconButton";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../kit/Menu";
import { ErrorState } from "../../ViewStates";
import {
  ConfirmDialog,
  DetailCard,
  DetailHead,
  Fact,
  Facts,
  IndexCard,
  IndexRow,
  Note,
  PageHead,
  QuietNote,
  SectionHead,
  SettingsPage,
  Split,
  SplitLoading,
  StatusLabel,
  TextAction,
} from "../parts/SettingsParts";
import { NeedBrand, useBrandHead } from "../parts/scope";
import { useSocialOAuthResult, type SocialOAuthResult } from "../socialOAuthPopup";
import { formatDay, formatMoment, listJoin, plural } from "../parts/settingsFormat";
import {
  PROVIDERS,
  connectionHealth,
  providerFromParam,
  providerInfo,
  socialErrorMessage,
  type ConnectionHealth,
  type ProviderInfo,
} from "./socialProviders";

type Selection = { kind: "account"; id: string } | { kind: "provider"; provider: SocialProvider };

type Row = {
  key: string;
  info: ProviderInfo;
  connection?: SocialConnection;
  account?: SocialAccount;
  health?: ConnectionHealth;
};

function PlatformMark({ info, picture, size = 36 }: { info: ProviderInfo; picture?: string | null; size?: 36 | 56 }) {
  const Glyph = info.icon;
  const [failed, setFailed] = useState(false);
  return (
    <span aria-hidden className="relative shrink-0" style={{ width: size, height: size }}>
      <span className="grid size-full place-items-center overflow-hidden rounded-full border border-co-line2 bg-co-surface-solid">
        {picture && !failed ? (
          <img src={picture} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="size-full object-cover" />
        ) : (
          <Glyph className={size === 56 ? "size-6 text-co-text2" : "size-4 text-co-text2"} />
        )}
      </span>
      {picture && !failed ? (
        <span className="absolute -bottom-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-co-surface ring-2 ring-co-surface">
          <Glyph className="size-2.5 text-co-text" />
        </span>
      ) : null}
    </span>
  );
}

const HEALTH_SUB: Record<ConnectionHealth, string> = {
  connected: "Connected",
  attention: "Needs attention",
  checking: "Checking",
};

export default function SocialAccountsPage() {
  const head = useBrandHead();
  const brandId = head.brand?.id ?? null;
  const brandName = head.brandName;
  const { selectedOrganization } = useCurrentUser();
  const role = selectedOrganization?.role ?? null;
  const canConnect = role === "Owner" || role === "Guest";
  const canDisconnect = role === "Owner";
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const connectionsQuery = useSocialConnections(brandId);
  const { data: stats } = useSocialConnectionStats(brandId);
  const { data: publishable } = usePublishableAccounts(brandId);
  const { data: members } = useOrganizationMembers(role && role !== "Guest" ? (selectedOrganization?.organization.id ?? null) : null);
  const owner = members?.find((m) => m.role === "Owner");

  const connectFacebook = useConnectFacebook(brandId);
  const connectInstagram = useConnectInstagram(brandId);
  const connectTikTok = useConnectTikTok(brandId);
  const connectYouTube = useConnectYouTube(brandId);
  const disconnect = useDisconnectSocialConnection(brandId);

  const [picked, setPicked] = useState<Selection | null>(null);
  const [connecting, setConnecting] = useState<SocialProvider | null>(null);
  const [lastError, setLastError] = useState<{ provider: SocialProvider | null; code: string } | null>(null);
  const [changeTarget, setChangeTarget] = useState<Row | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<Row | null>(null);

  const connectFor = (provider: SocialProvider) => {
    switch (provider) {
      case "INSTAGRAM":
        return connectInstagram;
      case "TIKTOK":
        return connectTikTok;
      case "YOUTUBE":
        return connectYouTube;
      default:
        return connectFacebook;
    }
  };

  const startConnect = (provider: SocialProvider) => {
    setLastError(null);
    setConnecting(provider);
    setPicked({ kind: "provider", provider });
    connectFor(provider).mutate(undefined, {
      onError: () => {
        setConnecting(null);
        queue.add({ title: "Could not start the sign-in", description: "Please try again.", variant: "error" });
      },
    });
  };

  const syncedIds = useMemo(() => new Set((stats ?? []).filter((s) => s.lastSyncedAt).map((s) => s.accountId)), [stats]);
  const statsById = useMemo(() => new Map((stats ?? []).map((s) => [s.accountId, s])), [stats]);
  const publishById = useMemo(() => new Map((publishable ?? []).map((a) => [a.accountId, a.canPublish])), [publishable]);
  const connectionsData = connectionsQuery.data;

  const rows: Row[] = useMemo(() => {
    const connections = connectionsData ?? [];
    const out: Row[] = [];
    for (const info of PROVIDERS) {
      const connection = connections.find((c) => c.provider === info.provider);
      const accounts = connection?.accounts ?? [];
      if (connection && accounts.length > 0) {
        for (const account of accounts) {
          out.push({
            key: `account:${account.id}`,
            info,
            connection,
            account,
            health: connectionHealth(connection, publishById.get(account.id), syncedIds.has(account.id)),
          });
        }
      } else {
        out.push({ key: `provider:${info.provider}`, info, connection });
      }
    }
    return out;
  }, [connectionsData, publishById, syncedIds]);

  /** Toasts and refetches for a sign-in result; no component state (the URL path runs in an effect). */
  const announce = useCallback(
    (result: SocialOAuthResult, provider: ProviderInfo | null, showError: boolean) => {
      if (result.connected) {
        const moved = result.moved ? Number(result.moved) : 0;
        queue.add({
          title: `${brandName} on ${provider?.label ?? "the platform"} added`,
          description:
            moved > 0
              ? `${plural(moved, "scheduled post")} moved to the new account.`
              : "It reads its posts for about a minute, then it is ready.",
          variant: "success",
        });
      } else if (result.socialError && showError) {
        queue.add({ title: "Nothing was connected", description: socialErrorMessage(result.socialError, provider), variant: "error" });
      }
      void queryClient.invalidateQueries({ queryKey: ["socialConnections", brandId] });
      void queryClient.invalidateQueries({ queryKey: ["socialConnectionStats", brandId] });
    },
    [brandId, brandName, queryClient],
  );

  /** The popup's result: the page that waited shows it in place. */
  const handlePopupResult = useCallback(
    (result: SocialOAuthResult) => {
      const provider = providerFromParam(result.connected) ?? (connecting ? providerInfo(connecting) : null);
      announce(result, provider, !provider);
      if (provider) setPicked({ kind: "provider", provider: provider.provider });
      setLastError(result.socialError ? { provider: provider?.provider ?? null, code: result.socialError } : null);
      setConnecting(null);
    },
    [announce, connecting],
  );

  useSocialOAuthResult(handlePopupResult);

  // The full-page redirect's result (a blocked popup, or an old link).
  const connectedParam = searchParams.get("connected");
  const errorParam = searchParams.get("socialError");
  const movedParam = searchParams.get("moved");
  useEffect(() => {
    if (!connectedParam && !errorParam && !movedParam) return;
    const result = {
      connected: connectedParam ?? undefined,
      socialError: errorParam ?? undefined,
      moved: movedParam ?? undefined,
    };
    announce(result, providerFromParam(connectedParam), true);
    const next = new URLSearchParams(searchParams);
    next.delete("connected");
    next.delete("socialError");
    next.delete("moved");
    next.set("page", "social-accounts");
    setSearchParams(next, { replace: true });
  }, [connectedParam, errorParam, movedParam, announce, searchParams, setSearchParams]);

  // Selection: what was picked if it still exists, else the first row.
  const selectedRow =
    (picked?.kind === "account" && rows.find((r) => r.account?.id === picked.id)) ||
    (picked?.kind === "provider" && rows.find((r) => r.info.provider === picked.provider)) ||
    rows.find((r) => r.health === "attention") ||
    rows[0];

  const publishing = rows.filter((r) => r.health === "connected").length;
  const attention = rows.filter((r) => r.health === "attention");
  const anyConnected = rows.some((r) => r.account);
  const status = !anyConnected
    ? `No accounts are connected to ${brandName} yet.`
    : [
        publishing === 1 ? "One account is publishing." : `${plural(publishing, "account")} publishing.`,
        attention.length
          ? `${listJoin([...new Set(attention.map((r) => r.info.label))])} ${attention.length === 1 ? "needs" : "need"} reconnecting.`
          : null,
        !canConnect ? `Only an owner connects or reconnects accounts${owner ? `; ${owner.name || owner.email} is the owner` : ""}.` : null,
      ]
        .filter(Boolean)
        .join(" ");

  const rowSub = (row: Row) => {
    if (connecting === row.info.provider) return "Connecting…";
    if (!row.account) {
      if (lastError?.provider === row.info.provider) return "Could not connect";
      return row.connection ? "Connected · no accounts" : "Not connected";
    }
    return `${row.info.label} · ${HEALTH_SUB[row.health ?? "connected"]}`;
  };

  return (
    <SettingsPage>
      <PageHead {...head} title="Social accounts" status={brandId ? status : undefined} />
      {!brandId ? (
        <NeedBrand />
      ) : connectionsQuery.isLoading ? (
        <SplitLoading caption={`Loading ${brandName}’s accounts`} rows={4} />
      ) : connectionsQuery.isError ? (
        <ErrorState
          title={`${brandName}’s accounts did not load`}
          body="Creo could not read them, so none is shown as connected or not. Scheduled posts are not affected."
          onRetry={() => void connectionsQuery.refetch()}
        />
      ) : (
        <Split
          backLabel="Social accounts"
          index={
            <IndexCard>
              {rows.map((row) => (
                <IndexRow
                  key={row.key}
                  lead={<PlatformMark info={row.info} picture={row.account?.pictureUrl} />}
                  title={row.account ? row.account.name || row.account.username || row.info.label : row.info.label}
                  sub={rowSub(row)}
                  subTone={row.health === "attention" ? "alert" : "muted"}
                  trail={row.health === "attention" ? <TriangleAlert aria-hidden className="size-3.5 shrink-0 text-co-alert-ink" /> : undefined}
                  active={row === selectedRow}
                  onPress={() =>
                    setPicked(row.account ? { kind: "account", id: row.account.id } : { kind: "provider", provider: row.info.provider })
                  }
                />
              ))}
            </IndexCard>
          }
          detail={
            !selectedRow ? null : connecting === selectedRow.info.provider ? (
              <ConnectingDetail
                info={selectedRow.info}
                brandName={brandName}
                onReopen={() => startConnect(selectedRow.info.provider)}
                onCancel={() => setConnecting(null)}
              />
            ) : selectedRow.account && selectedRow.connection ? (
              <AccountDetail
                row={selectedRow}
                lastSyncedAt={statsById.get(selectedRow.account.id)?.lastSyncedAt ?? null}
                canConnect={canConnect}
                canDisconnect={canDisconnect}
                ownerName={owner?.name || owner?.email}
                onReconnect={() => startConnect(selectedRow.info.provider)}
                onChange={() => setChangeTarget(selectedRow)}
                onDisconnect={() => setDisconnectTarget(selectedRow)}
              />
            ) : (
              <NotConnectedDetail
                info={selectedRow.info}
                brandName={brandName}
                canConnect={canConnect}
                error={lastError && lastError.provider === selectedRow.info.provider ? lastError.code : null}
                onConnect={() => startConnect(selectedRow.info.provider)}
              />
            )
          }
        />
      )}

      {changeTarget ? (
        <ConfirmDialog
          danger={false}
          title={`Change the ${changeTarget.info.label} account?`}
          description={`You sign in to ${changeTarget.info.label} again. If you pick a different account, the posts scheduled here move to it. Nothing is published to an account you did not pick.`}
          confirmLabel={`Continue to ${changeTarget.info.label}`}
          onClose={() => setChangeTarget(null)}
          onConfirm={() => {
            startConnect(changeTarget.info.provider);
            setChangeTarget(null);
          }}
        />
      ) : null}

      {disconnectTarget?.connection ? (
        <DisconnectDialog
          row={disconnectTarget}
          brandName={brandName}
          onClose={() => setDisconnectTarget(null)}
          onConfirm={() => {
            const target = disconnectTarget;
            disconnect.mutate(target.connection!.id, {
              onSuccess: () => queue.add({ title: `${target.info.label} disconnected from ${brandName}`, variant: "success" }),
              onError: () => queue.add({ title: "Could not disconnect", description: "It is still connected. Please try again.", variant: "error" }),
            });
            setPicked({ kind: "provider", provider: target.info.provider });
            setDisconnectTarget(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

function NotConnectedDetail({
  info,
  brandName,
  canConnect,
  error,
  onConnect,
}: {
  info: ProviderInfo;
  brandName: string;
  canConnect: boolean;
  error: string | null;
  onConnect: () => void;
}) {
  return (
    <DetailCard>
      <DetailHead
        lead={<PlatformMark info={info} size={56} />}
        title={info.label}
        sub={`For ${brandName}`}
        aside={<StatusLabel tone="muted">Not connected</StatusLabel>}
      />
      {error ? <Note tone={error === "access_denied" ? "info" : "alert"}>{socialErrorMessage(error, info)}</Note> : null}
      <SectionHead title="Before you connect" />
      <Facts>
        <Fact label="You need">{info.needs(brandName)}</Fact>
        <Fact label="Sign in with">{info.signIn}</Fact>
        <Fact label="Creo can">Publish the posts you schedule, and read how they do</Fact>
        <Fact label="After you sign in">{info.after(brandName)}</Fact>
      </Facts>
      {canConnect ? (
        <KitButton className="self-start" onClick={onConnect}>
          {error ? `Try ${info.label} again` : `Connect ${info.label}`}
        </KitButton>
      ) : (
        <QuietNote>Only an owner, or {brandName}’s client, connects accounts.</QuietNote>
      )}
    </DetailCard>
  );
}

function ConnectingDetail({
  info,
  brandName,
  onReopen,
  onCancel,
}: {
  info: ProviderInfo;
  brandName: string;
  onReopen: () => void;
  onCancel: () => void;
}) {
  const steps = [
    { label: `1 On ${info.label}`, body: "Sign in, then allow Creo to publish" },
    { label: "2 Back here", body: `The accounts are added to ${brandName}` },
    { label: "3 Then", body: "The account reads its posts for about a minute, then it is ready" },
  ];
  return (
    <DetailCard>
      <DetailHead
        lead={<PlatformMark info={info} size={56} />}
        title={`Finish on ${info.label}`}
        sub={`A ${info.label} window is open. Sign in with ${info.signIn.toLowerCase()}.`}
        aside={
          <StatusLabel icon={LoaderCircle} tone="muted" turning>
            Connecting
          </StatusLabel>
        }
      />
      <Facts>
        {steps.map((s) => (
          <Fact key={s.label} label={s.label}>
            {s.body}
          </Fact>
        ))}
      </Facts>
      <div className="flex items-center gap-3">
        <KitButton variant="secondary" onClick={onReopen}>
          Open the {info.label} window
        </KitButton>
        <KitButton variant="ghost" onClick={onCancel}>
          Cancel
        </KitButton>
      </div>
      <QuietNote>If you close the {info.label} window without finishing, nothing changes here.</QuietNote>
    </DetailCard>
  );
}

function AccountDetail({
  row,
  lastSyncedAt,
  canConnect,
  canDisconnect,
  ownerName,
  onReconnect,
  onChange,
  onDisconnect,
}: {
  row: Row;
  lastSyncedAt: string | null;
  canConnect: boolean;
  canDisconnect: boolean;
  ownerName?: string;
  onReconnect: () => void;
  onChange: () => void;
  onDisconnect: () => void;
}) {
  const { info, account, connection, health } = row;
  if (!account || !connection) return null;
  const name = account.name || account.username || info.label;
  const held = connection.pendingPublicationCount;
  const attention = health === "attention";

  return (
    <DetailCard>
      <DetailHead
        lead={<PlatformMark info={info} picture={account.pictureUrl} size={56} />}
        title={name}
        sub={[account.username ? `@${account.username}` : null, info.accountNoun].filter(Boolean).join(" · ")}
        aside={
          <>
            {attention ? (
              <StatusLabel icon={TriangleAlert} tone="alert">
                Needs attention
              </StatusLabel>
            ) : health === "checking" ? (
              <StatusLabel icon={LoaderCircle} tone="muted" turning>
                Checking
              </StatusLabel>
            ) : (
              <StatusLabel icon={Check}>Connected</StatusLabel>
            )}
            {canDisconnect ? (
              <KitMenuTrigger>
                <KitPressable>
                  <KitIconButton icon={Ellipsis} aria-label={`More for ${name}`} />
                </KitPressable>
                <KitMenuPopover>
                  <KitMenu aria-label="More" onAction={(key) => key === "disconnect" && onDisconnect()}>
                    <KitMenuItem id="disconnect" icon={Unplug} danger>
                      Disconnect
                    </KitMenuItem>
                  </KitMenu>
                </KitMenuPopover>
              </KitMenuTrigger>
            ) : null}
          </>
        }
      />

      {attention ? (
        <Note
          tone="alert"
          actions={
            canConnect ? (
              <KitButton iconLeft={RefreshCw} onClick={onReconnect}>
                Reconnect
              </KitButton>
            ) : null
          }
        >
          {connection.authError
            ? `${connection.authError} `
            : `${info.label} needs this login signed in again before it can publish. `}
          {held > 0
            ? `The ${plural(held, "post")} scheduled here ${held === 1 ? "is" : "are"} held until then, and nothing is sent to another account.`
            : "Nothing is sent to another account."}
          {!canConnect ? ` Only an owner can reconnect${ownerName ? `: ask ${ownerName}` : ""}.` : ""}
        </Note>
      ) : canConnect ? (
        <KitButton variant="secondary" className="self-start" onClick={onChange}>
          Change account
        </KitButton>
      ) : null}

      <Facts>
        <Fact label="Can publish">
          {attention ? "Not until it is reconnected" : health === "checking" ? "Checking. This is confirmed when the read finishes" : `Yes: ${info.canPublish}`}
        </Fact>
        <Fact label="Last synced">{lastSyncedAt ? formatMoment(lastSyncedAt) : attention ? "Not yet" : "Reading now"}</Fact>
        <Fact label="Connected">{formatDay(connection.createdAt)}</Fact>
        {connection.accounts.length > 1 ? (
          <Fact label="Same login">
            {listJoin(connection.accounts.filter((a) => a.id !== account.id).map((a) => a.name || a.username || info.label))}
          </Fact>
        ) : null}
      </Facts>

      <SectionHead title={attention ? "Waiting on this account" : "Scheduled on this account"} meta={held > 0 ? plural(held, "post") : undefined} />
      <p className={cn("text-[13.5px]", held > 0 ? "text-co-text2" : "text-co-muted")}>
        {held > 0
          ? `${plural(held, "post")} ${held === 1 ? "is" : "are"} scheduled to go out from this account.`
          : `Nothing yet. Posts scheduled for ${info.label} from the calendar count here.`}
      </p>
      <TextAction icon={CalendarDays} to="/dashboard/content-calendar">
        Open the calendar
      </TextAction>
    </DetailCard>
  );
}

function DisconnectDialog({
  row,
  brandName,
  onClose,
  onConfirm,
}: {
  row: Row;
  brandName: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { info, connection, account } = row;
  const held = connection?.pendingPublicationCount ?? 0;
  const others = (connection?.accounts.length ?? 1) - 1;
  const word = (account?.username || account?.name || info.label).toLowerCase();
  return (
    <ConfirmDialog
      title={`Disconnect ${account?.name || brandName} on ${info.label}?`}
      description={`${held > 0 ? `${plural(held, "scheduled post")} ${held === 1 ? "is" : "are"} cancelled. ` : ""}Posts already published stay on ${info.label}.`}
      consequences={[
        ...(held > 0 ? [{ icon: CalendarX, text: `${plural(held, "scheduled post")} ${held === 1 ? "is" : "are"} cancelled.` }] : []),
        { icon: ChartLine, text: "Results stop updating. What was read so far stays." },
        ...(others > 0
          ? [{ icon: Unplug, text: `The ${plural(others, `other ${info.label} account`)} from the same login ${others === 1 ? "goes" : "go"} too.` }]
          : []),
      ]}
      typeToConfirm={word}
      cancelLabel="Keep it"
      confirmLabel="Disconnect"
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}
